import { GetTokenSilentlyOptions, RedirectLoginOptions } from '@auth0/auth0-spa-js';

import { NotificationsUrls } from '@/constants/notifications';
import { getAuth0Client } from '@/services/auth';
import { loginToAdminApiOms } from '@/services/oms/admin/auth/api';
import { loginToShopApiOms } from '@/services/oms/shop/auth/api';

import { AuthState } from './auth.state';

export const setupAuthActions = (state: AuthState) => {
  const isKnockUserTokenNeeded = !import.meta.env.VITE_KNOCK_API_KEY?.includes('test');

  /**
   * Refresh user token or redirect to login page if token is expired.
   *
   * @returns - Auth0 token if authenticated, otherwise redirects to auth0 login page.
   */
  async function login(options?: GetTokenSilentlyOptions | RedirectLoginOptions): Promise<string | false> {
    const auth0 = getAuth0Client();

    if (!auth0) {
      return false;
    }

    try {
      const [accessToken] = await Promise.all([
        auth0.getTokenSilently(options),
        ...(import.meta.env.VITE_ENABLE_OMS_DEMO && import.meta.env.VITE_ENABLE_OMS_GUEST_CHECKOUT
          ? [loginToAdminApiOms(), loginToShopApiOms()]
          : []),
      ]);
      const user = await auth0.getUser();

      state.accessToken.value = accessToken;
      state.userId.value = user?.sub || '';

      //  We only need to provide the knock user token if we are not in development or testing mode
      state.knockUserToken.value = isKnockUserTokenNeeded ? user?.[NotificationsUrls.KnockUserTokenUrl] : '';
    } catch (err) {
      const path = window.location.pathname;
      const redirect = new URLSearchParams(window.location.search).get('redirect');

      auth0.loginWithRedirect({
        appState: {
          target: redirect || path,
        },
      });

      return false;
    }

    state.isAuthenticated.value = true;

    return state.accessToken.value;
  }

  function logout() {
    window.location.href = `${window.location.origin}/accounts/logout/`;
  }

  return {
    login,
    logout,
  };
};
